import {useAccessToken, useIsBankUser, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {getAssociatedBanks, getOrg, getOrgSettings} from "../../resources/org"
import Loading from "../../components/Loading/Loading"
import React from "react"
import {Customer, findCustomers} from "../../resources/customer"
import {useParams} from "react-router"
import {AsyncResult} from "../../types/asyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {ok} from "neverthrow"
import {hasPermission} from "../../services/permission"
import OrgPageForBankUser from "./OrgPageForBankUser/OrgPageForBankUser"
import OrgPageForUnitUser from "./OrgPageForUnitUser/OrgPageForUnitUser"

export default function OrgPage() {
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const {orgId = ""} = useParams<{orgId: string}>()

	const org = useAsyncResult(() => getOrg(accessToken, orgId), [refreshToken])

	const emptyPromise = Promise.resolve(ok(null))
	const hasOrgSettingsPermission = hasPermission("orgSettings", "get")
	const hasBankPermission = hasPermission("org.associatedBanks", "get")

	const orgSettings = useAsyncResult(
		() => (hasOrgSettingsPermission ? getOrgSettings(accessToken, orgId) : emptyPromise),
		[refreshToken, hasOrgSettingsPermission]
	)
	const associatedBanks = useAsyncResult(
		() => (hasBankPermission ? getAssociatedBanks(accessToken, orgId) : emptyPromise),
		[refreshToken, hasBankPermission]
	)

	const customers = useAsyncResult(() => findCustomers(accessToken, 0, 1000, "-createdAt", "", ["1"]))

	return AsyncResult.zip4(org, orgSettings, associatedBanks, customers).match(
		() => <Loading />,
		([org, orgSettings, associatedBanks, customers]) => {
			const commonProps = {
				org,
				currentDefaultBankName: orgSettings?.attributes.fallbackBankName,
				customers: (customers as {data: Customer[]}).data,
				bankNames: associatedBanks?.attributes?.banks?.map((bank) => bank.name) ?? [],
				refresh,
				refreshToken,
			}
			const isUnitUser = useIsUnitUser()
			const isBankUser = useIsBankUser()
			if (isUnitUser) {
				return <OrgPageForUnitUser {...commonProps} />
			}
			if (isBankUser) {
				return <OrgPageForBankUser {...commonProps} />
			}
			return <></>
		},
		(_) => null
	)
}
